<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center flex-wrap">
        <div class="mb-50">
          <h1 class="font-weight-bolder mb-0">
            Inicio de sesión
          </h1>
          <small class="text-muted">
            Imágenes que ven los clientes al entrar. Con más de una se muestran como carrusel;
            si ninguna está visible, se usa la imagen de YoConstruyo.
          </small>
        </div>
        <div class="mb-50 text-right">
          <b-button
            variant="primary"
            :disabled="loading || atLimit"
            @click="openEditor()"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            Agregar imagen
          </b-button>
          <small
            v-if="atLimit"
            class="d-block text-muted mt-25"
          >
            Llegaste al máximo de {{ maxSlides }} imágenes; elimina una para agregar otra.
          </small>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        lg="7"
        class="mb-2"
      >
        <b-overlay
          :show="loading || busy"
          rounded="sm"
        >
          <b-card
            v-if="!loading && !slides.length"
            class="text-center"
          >
            <feather-icon
              icon="ImageIcon"
              size="32"
              class="text-muted mb-1"
            />
            <h5 class="mb-50">
              Todavía no hay imágenes
            </h5>
            <p class="text-muted mb-1">
              Hoy se muestra la imagen de YoConstruyo. Agrega una o varias para cambiarla o
              convertirla en carrusel.
            </p>
            <b-button
              variant="outline-primary"
              @click="openEditor()"
            >
              Agregar la primera
            </b-button>
          </b-card>

          <b-card
            v-for="(slide, index) in slides"
            :key="slide.id"
            no-body
            :class="['login-slide-row mb-1', { 'login-slide-row--hidden': !slide.active }]"
          >
            <div class="login-slide-row__body">
              <div class="login-slide-row__order">
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon"
                  :disabled="index === 0 || busy"
                  :aria-label="`Mover la imagen ${index + 1} hacia arriba`"
                  @click="move(index, -1)"
                >
                  <feather-icon icon="ChevronUpIcon" />
                </b-button>
                <span class="login-slide-row__position">{{ index + 1 }}</span>
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon"
                  :disabled="index === slides.length - 1 || busy"
                  :aria-label="`Mover la imagen ${index + 1} hacia abajo`"
                  @click="move(index, 1)"
                >
                  <feather-icon icon="ChevronDownIcon" />
                </b-button>
              </div>

              <button
                type="button"
                class="login-slide-row__thumb"
                :style="{ backgroundImage: `url('${imageSrc(slide.image_url)}')` }"
                :aria-label="`Editar la imagen ${index + 1}`"
                @click="openEditor(slide)"
              />

              <div class="login-slide-row__info">
                <div class="font-weight-bolder text-truncate">
                  {{ slideTitle(slide) }}
                </div>
                <small class="text-muted d-block text-truncate">
                  {{ slideSummary(slide) }}
                </small>
                <b-badge
                  :variant="slide.active ? 'light-success' : 'light-secondary'"
                  class="mt-50"
                >
                  {{ slide.active ? 'Visible' : 'Oculta' }}
                </b-badge>
              </div>

              <div class="login-slide-row__actions">
                <b-form-checkbox
                  :checked="slide.active"
                  switch
                  :disabled="busy"
                  :aria-label="slide.active ? 'Ocultar del inicio de sesión' : 'Mostrar en el inicio de sesión'"
                  @change="toggleActive(slide, $event)"
                />
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon"
                  :disabled="busy"
                  aria-label="Editar"
                  @click="openEditor(slide)"
                >
                  <feather-icon icon="Edit2Icon" />
                </b-button>
                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon"
                  :disabled="busy"
                  aria-label="Eliminar"
                  @click="remove(slide)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </div>
          </b-card>
        </b-overlay>
      </b-col>

      <b-col lg="5">
        <div class="login-slides-aside">
          <h6 class="text-uppercase text-muted mb-1">
            Así se ve hoy
          </h6>
          <auth-showcase-preview
            :slides="visibleSlides"
            :max-height="asidePreviewHeight"
          />
          <small class="text-muted d-block mt-50">
            {{ previewNote }}
          </small>
        </div>
      </b-col>
    </b-row>

    <b-modal
      v-model="editorOpen"
      size="xl"
      centered
      no-close-on-backdrop
      :title="draft.id ? 'Editar imagen' : 'Agregar imagen'"
      @hidden="closeEditor"
    >
      <b-row>
        <b-col
          lg="6"
          class="mb-1"
        >
          <b-alert
            :show="Boolean(errorMessage)"
            variant="danger"
          >
            <div class="alert-body">
              {{ errorMessage }}
            </div>
          </b-alert>

          <b-form-group
            label="Imagen"
            label-for="login-slide-image"
            description="JPG, PNG o WebP de al menos 700 × 800 px; recomendada vertical, de 1200 × 1500 px. Al elegirla la ajustamos y comprimimos. Se recorta para llenar el panel, siempre se conserva la parte de arriba y el texto va sobre el lado izquierdo."
          >
            <b-form-file
              id="login-slide-image"
              :key="fileInputKey"
              accept="image/jpeg,image/png,image/webp"
              :placeholder="draft.id ? 'Conservar la imagen actual' : 'Elige una imagen o arrástrala aquí'"
              drop-placeholder="Suelta la imagen aquí"
              browse-text="Buscar"
              :disabled="preparingImage"
              @input="pickImage"
            />
            <small
              v-if="preparingImage"
              class="d-block text-muted mt-50"
            >
              Preparando la imagen...
            </small>
            <small
              v-else-if="imageInfo"
              class="d-block text-success mt-50"
            >
              {{ imageInfo }}
            </small>
          </b-form-group>

          <b-form-group
            label="Frase corta"
            label-for="login-slide-kicker"
            description="Va en mayúsculas arriba del título; cada punto abre un renglón."
          >
            <b-form-input
              id="login-slide-kicker"
              v-model="draft.kicker"
              maxlength="60"
              placeholder="Cotiza. Compara. Compra."
            />
          </b-form-group>

          <b-row>
            <b-col sm="7">
              <b-form-group
                label="Título"
                label-for="login-slide-title"
              >
                <b-form-input
                  id="login-slide-title"
                  v-model="draft.title"
                  maxlength="80"
                  placeholder="Tu asistente de compras"
                />
              </b-form-group>
            </b-col>
            <b-col sm="5">
              <b-form-group
                label="Remate en verde"
                label-for="login-slide-highlight"
              >
                <b-form-input
                  id="login-slide-highlight"
                  v-model="draft.highlight"
                  maxlength="40"
                  placeholder="digital"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Frases con icono">
            <b-input-group
              v-for="(bullet, index) in draft.bullets"
              :key="bullet.key"
              class="mb-50"
            >
              <b-input-group-prepend>
                <b-dropdown variant="outline-secondary">
                  <template #button-content>
                    <feather-icon :icon="bullet.icon" />
                    <span class="sr-only">Icono de la frase {{ index + 1 }}: {{ iconLabel(bullet.icon) }}</span>
                  </template>
                  <b-dropdown-item
                    v-for="option in iconOptions"
                    :key="option.icon"
                    :active="bullet.icon === option.icon"
                    @click="bullet.icon = option.icon"
                  >
                    <feather-icon
                      :icon="option.icon"
                      class="mr-50"
                    />
                    {{ option.label }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-input-group-prepend>
              <b-form-input
                v-model="bullet.text"
                maxlength="60"
                :placeholder="bulletPlaceholders[index]"
                :aria-label="`Frase ${index + 1}`"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :aria-label="`Quitar la frase ${index + 1}`"
                  @click="draft.bullets.splice(index, 1)"
                >
                  <feather-icon icon="XIcon" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button
              v-if="draft.bullets.length < maxBullets"
              variant="flat-primary"
              size="sm"
              @click="addBullet"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-25"
              />
              Agregar frase
            </b-button>
          </b-form-group>

          <b-form-checkbox
            v-model="draft.active"
            switch
          >
            Visible en el inicio de sesión
          </b-form-checkbox>
          <small class="text-muted d-block mt-1">
            Todos los textos son opcionales: sin texto, la imagen se muestra sola, sin
            oscurecerla. Si la imagen ya trae su propio texto, déjalos vacíos.
          </small>
        </b-col>

        <b-col lg="6">
          <auth-showcase-preview
            v-if="draftImageUrl"
            :slides="[draftSlide]"
            :max-height="editorPreviewHeight"
          />
          <div
            v-else
            class="login-slides-empty-preview"
          >
            <feather-icon
              icon="ImageIcon"
              size="28"
              class="mb-50"
            />
            Elige una imagen para ver cómo queda
          </div>
        </b-col>
      </b-row>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="saving"
          @click="editorOpen = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || preparingImage"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
          />
          Guardar
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BOverlay, BBadge, BModal, BAlert, BFormGroup, BFormInput,
  BFormFile, BFormCheckbox, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BDropdown,
  BDropdownItem, BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AuthShowcasePreview from '@/components/auth/AuthShowcasePreview.vue'
import assetUrl from '@/utils/assetUrl'
import normalizeImage from '@/utils/normalizeImage'

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_SOURCE_BYTES = 20 * 1024 * 1024
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024
const MIN_WIDTH = 700
const MIN_HEIGHT = 800

const emptyDraft = () => ({
  id: null,
  image: null,
  image_url: '',
  kicker: '',
  title: '',
  highlight: '',
  bullets: [],
  active: true,
})

let bulletSeed = 0
const withKey = bullet => {
  bulletSeed += 1
  return { key: bulletSeed, icon: bullet.icon || 'CheckCircleIcon', text: bullet.text || '' }
}

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BOverlay,
    BBadge,
    BModal,
    BAlert,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormCheckbox,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BDropdown,
    BDropdownItem,
    BSpinner,
    AuthShowcasePreview,
  },

  data() {
    return {
      loading: false,
      busy: false,
      saving: false,
      editorOpen: false,
      errorMessage: '',
      draft: emptyDraft(),
      localImageUrl: '',
      imageInfo: '',
      preparingImage: false,
      fileInputKey: 0,
      maxSlides: 10,
      viewportHeight: window.innerHeight,
      maxBullets: 3,
      bulletPlaceholders: [
        'Encuentra los mejores precios',
        'Compara proveedores en minutos',
        'Compra con confianza',
      ],
      iconOptions: [
        { icon: 'SearchIcon', label: 'Lupa' },
        { icon: 'BoxIcon', label: 'Caja' },
        { icon: 'TruckIcon', label: 'Camión' },
        { icon: 'ShieldIcon', label: 'Escudo' },
        { icon: 'TagIcon', label: 'Etiqueta' },
        { icon: 'ClockIcon', label: 'Reloj' },
        { icon: 'CheckCircleIcon', label: 'Palomita' },
        { icon: 'DollarSignIcon', label: 'Dinero' },
      ],
    }
  },

  computed: {
    ...mapGetters('loginSlides', { slides: 'adminSlides' }),

    atLimit() {
      return this.slides.length >= this.maxSlides
    },

    visibleSlides() {
      return this.slides.filter(slide => slide.active)
    },

    previewNote() {
      const count = this.visibleSlides.length
      if (!count) return 'Ninguna imagen visible: se muestra la de YoConstruyo.'
      if (count === 1) return 'Una sola imagen visible: se muestra fija, sin carrusel.'
      return `${count} imágenes visibles: cambian cada 6 segundos, en el orden de la lista.`
    },

    asidePreviewHeight() {
      return Math.max(320, this.viewportHeight - 190)
    },

    editorPreviewHeight() {
      return Math.max(320, this.viewportHeight - 230)
    },

    draftImageUrl() {
      return this.localImageUrl || this.imageSrc(this.draft.image_url)
    },

    draftSlide() {
      return {
        id: 'draft',
        image_url: this.draftImageUrl,
        kicker: this.draft.kicker.trim(),
        title: this.draft.title.trim(),
        highlight: this.draft.highlight.trim(),
        bullets: this.cleanBullets(),
      }
    },
  },

  created() {
    this.load()
  },

  mounted() {
    window.addEventListener('resize', this.trackViewport)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.trackViewport)
    this.releaseLocalImage()
  },

  methods: {
    ...mapActions('loginSlides', [
      'fetchAdminLoginSlides',
      'createLoginSlide',
      'updateLoginSlide',
      'deleteLoginSlide',
      'reorderLoginSlides',
    ]),

    trackViewport() {
      this.viewportHeight = window.innerHeight
    },

    iconLabel(icon) {
      const option = this.iconOptions.find(item => item.icon === icon)
      return option ? option.label : icon
    },

    imageSrc(url) {
      return assetUrl(url, this.$store.state.apiUrl)
    },

    slideTitle(slide) {
      const title = [slide.title, slide.highlight].filter(Boolean).join(' ')
      return title || slide.kicker || 'Solo imagen'
    },

    slideSummary(slide) {
      const bullets = (slide.bullets || []).length
      if (!bullets) return slide.title && slide.kicker ? slide.kicker : 'Sin frases'
      return bullets === 1 ? '1 frase con icono' : `${bullets} frases con icono`
    },

    cleanBullets() {
      return this.draft.bullets
        .map(bullet => ({ icon: bullet.icon, text: bullet.text.trim() }))
        .filter(bullet => bullet.text)
    },

    notify(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },

    errorFrom(error, fallback) {
      const messages = error.response?.data?.messages
      return Array.isArray(messages) && messages.length ? messages.join('. ') : fallback
    },

    async load() {
      this.loading = true
      try {
        await this.fetchAdminLoginSlides()
      } finally {
        this.loading = false
      }
    },

    async runBusy(task, fallback) {
      this.busy = true
      try {
        await task()
      } catch (error) {
        this.notify(this.errorFrom(error, fallback), 'danger')
        await this.fetchAdminLoginSlides().catch(() => {})
      } finally {
        this.busy = false
      }
    },

    move(index, step) {
      const ids = this.slides.map(slide => slide.id)
      const target = index + step
      if (target < 0 || target >= ids.length) return
      const [moved] = ids.splice(index, 1)
      ids.splice(target, 0, moved)
      this.runBusy(() => this.reorderLoginSlides(ids), 'No se pudo cambiar el orden')
    },

    toggleActive(slide, active) {
      this.runBusy(
        () => this.updateLoginSlide({ id: slide.id, active }),
        'No se pudo cambiar la visibilidad',
      )
    },

    async remove(slide) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'La imagen deja de mostrarse en el inicio de sesión y no se puede recuperar.',
        {
          title: '¿Eliminar esta imagen?',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return
      await this.runBusy(async () => {
        await this.deleteLoginSlide(slide.id)
        this.notify('Imagen eliminada')
      }, 'No se pudo eliminar la imagen')
    },

    openEditor(slide) {
      this.releaseLocalImage()
      this.errorMessage = ''
      this.fileInputKey += 1
      this.draft = slide
        ? {
          ...emptyDraft(),
          id: slide.id,
          image_url: slide.image_url,
          kicker: slide.kicker || '',
          title: slide.title || '',
          highlight: slide.highlight || '',
          bullets: (slide.bullets || []).map(withKey),
          active: slide.active,
        }
        : emptyDraft()
      this.editorOpen = true
    },

    closeEditor() {
      this.releaseLocalImage()
      this.draft = emptyDraft()
      this.errorMessage = ''
    },

    releaseLocalImage() {
      if (this.localImageUrl) URL.revokeObjectURL(this.localImageUrl)
      this.localImageUrl = ''
      this.imageInfo = ''
    },

    rejectImage(message) {
      this.errorMessage = message
      this.fileInputKey += 1
    },

    async pickImage(file) {
      this.releaseLocalImage()
      this.draft.image = null
      if (!file) return
      if (!IMAGE_TYPES.includes(file.type)) {
        this.rejectImage('La imagen debe ser JPG, PNG o WebP')
        return
      }
      if (file.size > MAX_SOURCE_BYTES) {
        this.rejectImage('La imagen pesa más de 20 MB; usa una más ligera')
        return
      }

      this.preparingImage = true
      try {
        const result = await normalizeImage(file, { name: 'inicio-de-sesion.jpg' })
        if (result.width < MIN_WIDTH || result.height < MIN_HEIGHT) {
          this.rejectImage(`La imagen mide ${result.sourceWidth} × ${result.sourceHeight} px; necesita al menos ${MIN_WIDTH} × ${MIN_HEIGHT} px para verse nítida. Usa una más grande o más vertical.`)
          return
        }
        if (result.file.size > MAX_UPLOAD_BYTES) {
          this.rejectImage('La imagen sigue pesando más de 5 MB después de comprimirla; usa otra')
          return
        }
        this.errorMessage = ''
        this.draft.image = result.file
        this.localImageUrl = URL.createObjectURL(result.file)
        this.imageInfo = `Lista para subir: ${result.width} × ${result.height} px, ${Math.max(1, Math.round(result.file.size / 1024))} KB`
      } catch (error) {
        this.rejectImage('No pudimos leer la imagen; prueba con otro archivo')
      } finally {
        this.preparingImage = false
      }
    },

    addBullet() {
      if (this.draft.bullets.length >= this.maxBullets) return
      this.draft.bullets.push(withKey({}))
    },

    async save() {
      if (!this.draft.id && !this.draft.image) {
        this.errorMessage = 'Elige una imagen'
        return
      }
      const payload = {
        kicker: this.draft.kicker.trim(),
        title: this.draft.title.trim(),
        highlight: this.draft.highlight.trim(),
        bullets: this.cleanBullets(),
        active: this.draft.active,
      }
      if (this.draft.image) payload.image = this.draft.image

      this.saving = true
      this.errorMessage = ''
      try {
        if (this.draft.id) {
          await this.updateLoginSlide({ id: this.draft.id, ...payload })
        } else {
          await this.createLoginSlide(payload)
        }
        this.editorOpen = false
        this.notify('Imagen guardada')
      } catch (error) {
        this.errorMessage = this.errorFrom(error, 'No se pudo guardar la imagen')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.login-slide-row__body {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem 0.75rem 0.5rem;
}

.login-slide-row--hidden .login-slide-row__thumb,
.login-slide-row--hidden .login-slide-row__info {
  opacity: 0.55;
}

.login-slide-row__order {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.login-slide-row__position {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
}

.login-slide-row__thumb {
  flex-shrink: 0;
  width: 84px;
  height: 100px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background-color: #0b0f14;
  background-size: cover;
  background-position: center top;
  cursor: pointer;
}

.login-slide-row__info {
  flex: 1 1 0;
  min-width: 0;
}

.login-slide-row__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.25rem;
}

.login-slides-aside {
  position: sticky;
  top: 6rem;
}

.login-slides-empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  height: 100%;
  border: 2px dashed #d0d5dd;
  border-radius: 14px;
  color: #98a2b3;
  text-align: center;
}

@media (max-width: 575.98px) {
  .login-slide-row__body {
    flex-wrap: wrap;
  }

  .login-slide-row__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
